import { useEffect, useMemo, useState } from "react";
import { Empty, Form, Modal, Select, Skeleton, Typography } from "antd";
import { Apartamento } from "../../../interfaces/Apartamento";
import { buscarAptosByEmpreendService } from "../../../services/buscarAptosByEmpreend";
import { copiarCotasApartamentoService } from "../../../services/copiarCotasApartamentoService";
import { formStyle, layout } from "../styles";

interface ModalCopiarProps {
  isModalOpen: boolean;
  handleFechar: () => void;
  aptoDestinoId: number | null;
  aptoDestinoNome: string | null;
  empreendimentoId: number | null;
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
}

export default function ModalCopiar({
  isModalOpen,
  handleFechar,
  aptoDestinoId,
  aptoDestinoNome,
  empreendimentoId,
  onSuccess,
  onError,
}: ModalCopiarProps) {
  const [form] = Form.useForm();
  const [apartamentos, setApartamentos] = useState<Apartamento[]>([]);
  const [isLoadingApartamentos, setIsLoadingApartamentos] = useState(false);
  const [isCopiando, setIsCopiando] = useState(false);

  const apartamentosOrigem = useMemo(
    () =>
      apartamentos.filter(
        (apartamento) => Number(apartamento.id) !== Number(aptoDestinoId)
      ),
    [apartamentos, aptoDestinoId]
  );

  useEffect(() => {
    const buscarApartamentos = async () => {
      if (!isModalOpen) {
        return;
      }

      if (!empreendimentoId) {
        setApartamentos([]);
        return;
      }

      setIsLoadingApartamentos(true);

      try {
        const data = await buscarAptosByEmpreendService(empreendimentoId);
        setApartamentos(data ?? []);
      } catch (error) {
        setApartamentos([]);
        onError("Nao foi possivel buscar os apartamentos do empreendimento.");
      } finally {
        setIsLoadingApartamentos(false);
      }
    };

    buscarApartamentos();
  }, [empreendimentoId, isModalOpen, onError]);

  useEffect(() => {
    if (!isModalOpen) {
      form.resetFields();
    }
  }, [form, isModalOpen]);

  const onCancel = () => {
    form.resetFields();
    handleFechar();
  };

  const onOk = async () => {
    try {
      const values = await form.validateFields();

      if (!aptoDestinoId) {
        onError("Nao foi possivel identificar o apartamento de destino.");
        return;
      }

      setIsCopiando(true);

      const response = await copiarCotasApartamentoService({
        aptoOrigemId: Number(values.aptoOrigemId),
        aptoDestinoId: Number(aptoDestinoId),
      });

      const mensagem =
        response?.message || "Cotas e periodos copiados com sucesso.";

      form.resetFields();
      handleFechar();
      onSuccess(mensagem);
    } catch (error: any) {
      if (error?.errorFields) {
        return;
      }

      const mensagemArray = error?.message;
      const mensagem =
        (Array.isArray(mensagemArray) ? mensagemArray[0] : mensagemArray) ||
        error?.error ||
        error?.mensagem ||
        "Nao foi possivel copiar as cotas do apartamento.";

      onError(mensagem);
    } finally {
      setIsCopiando(false);
    }
  };

  return (
    <Modal
      open={isModalOpen}
      onCancel={onCancel}
      onOk={onOk}
      okText="Copiar cotas"
      cancelText="Cancelar"
      confirmLoading={isCopiando}
      okButtonProps={{
        disabled:
          isLoadingApartamentos ||
          !aptoDestinoId ||
          apartamentosOrigem.length === 0,
        style: {
          backgroundColor: "#003644",
          borderColor: "#003644",
          color: "white",
        },
      }}
      cancelButtonProps={{
        style: {
          backgroundColor: "#f44336",
          borderColor: "#f44336",
          color: "white",
        },
      }}
      destroyOnClose
    >
      {isLoadingApartamentos ? (
        <Skeleton active />
      ) : (
        <>
          <Typography.Title level={4}>Copiar cotas</Typography.Title>
          <Typography.Paragraph>
            Selecione o apartamento de origem para copiar as cotas para{" "}
            <strong>{aptoDestinoNome || "o apartamento selecionado"}</strong>.
          </Typography.Paragraph>

          {apartamentosOrigem.length === 0 ? (
            <Empty description="Nenhum apartamento de origem disponivel neste empreendimento." />
          ) : (
            <Form
              {...layout}
              form={form}
              name="copiarCotasApartamento"
              style={formStyle}
            >
              <Form.Item
                name="aptoOrigemId"
                label="Apto origem"
                className="formItems"
                rules={[
                  {
                    required: true,
                    message: "Selecione o apartamento de origem",
                  },
                ]}
              >
                <Select
                  placeholder="Selecione o apartamento de origem"
                  options={apartamentosOrigem.map((apartamento) => ({
                    label: apartamento.nome,
                    value: apartamento.id,
                  }))}
                  showSearch
                  optionFilterProp="label"
                />
              </Form.Item>
            </Form>
          )}
        </>
      )}
    </Modal>
  );
}
